import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import styles from './ContextMenu.module.css';
import { useCanvas } from '../../../context/canvasContext';
import { BringToFrontIcon, SendToBackIcon, DuplicateIcon, GroupIcon, UngroupIcon, PropertiesIcon, DeleteIcon } from './icons';

const EDGE = 8;

/** Keeps the menu inside the viewport, flipping it up/left when it would overflow. */
const usePlacement = (menuRef, x, y) => {
    const [style, setStyle] = useState({ left: x, top: y });
    useLayoutEffect(() => {
        const menu = menuRef.current;
        if (!menu) return;
        const { offsetWidth: w, offsetHeight: h } = menu;
        setStyle({
            left: Math.max(EDGE, Math.min(x, window.innerWidth - w - EDGE)),
            top: Math.max(EDGE, Math.min(y, window.innerHeight - h - EDGE)),
        });
    }, [menuRef, x, y]);
    return style;
};

const Item = ({ icon, label, onClick, danger = false }) => (
    <button type="button" role="menuitem" className={`${styles.item} ${danger ? styles.itemDanger : ''}`} onClick={onClick}>
        <span className={styles.icon}>{icon}</span>
        {label}
    </button>
);

/**
 * Right-click menu for the selected object, opened by the canvas context at
 * the pointer. Closes on any choice, an outside click, Escape or scroll.
 */
const ContextMenu = () => {
    const {
        contextMenu, closeContextMenu, setIsPropertiesDrawerOpen,
        bringToFront, sendToBack, duplicateSelection, handleGroup, handleUngroup, deleteSelected,
    } = useCanvas();
    const menuRef = useRef(null);
    const style = usePlacement(menuRef, contextMenu?.x ?? 0, contextMenu?.y ?? 0);

    useEffect(() => {
        if (!contextMenu) return undefined;
        // Right presses are left to the canvas: the one that opened this menu
        // is still bubbling when the listener attaches, and another re-opens it.
        const onPointerDown = (e) => {
            if (e.button === 2 || menuRef.current?.contains(e.target)) return;
            closeContextMenu();
        };
        const onKeyDown = (e) => { if (e.key === 'Escape') closeContextMenu(); };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        window.addEventListener('wheel', closeContextMenu, { passive: true });
        window.addEventListener('resize', closeContextMenu);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('wheel', closeContextMenu);
            window.removeEventListener('resize', closeContextMenu);
        };
    }, [contextMenu, closeContextMenu]);

    if (!contextMenu) return null;

    const run = (action) => () => { action(); closeContextMenu(); };
    const openProperties = run(() => setIsPropertiesDrawerOpen(true));

    return (
        <div ref={menuRef} className={styles.menu} style={style} role="menu" aria-label="Object actions">
            <Item icon={<BringToFrontIcon />} label="Bring to front" onClick={run(bringToFront)} />
            <Item icon={<SendToBackIcon />} label="Send to back" onClick={run(sendToBack)} />
            <span className={styles.divider} aria-hidden="true" />
            <Item icon={<DuplicateIcon />} label="Duplicate" onClick={run(duplicateSelection)} />
            <Item icon={<GroupIcon />} label="Group" onClick={run(handleGroup)} />
            <Item icon={<UngroupIcon />} label="Ungroup" onClick={run(handleUngroup)} />
            {contextMenu.isShape && (
                <>
                    <span className={styles.divider} aria-hidden="true" />
                    <Item icon={<PropertiesIcon />} label="Properties" onClick={openProperties} />
                </>
            )}
            <span className={styles.divider} aria-hidden="true" />
            <Item icon={<DeleteIcon />} label="Delete" onClick={run(deleteSelected)} danger />
        </div>
    );
};

export default ContextMenu;
